import { useQuery } from "@apollo/client";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName, WEB_ORDERS_RESOURCE } from "../constants/resources";
import {
  CrmObject,
  MarketingWebOrdersDocument,
  type MarketingWebOrdersQuery,
  WebOrderStepKind,
} from "../generated/graphql";
import {
  CRM_COLORS,
  CRM_OBJECT_COLORS,
  crmRecordPath,
  formatCrmCount,
  formatCrmDate,
  formatCrmDateTime,
  formatCrmDayOf,
} from "../lib/crm";
import { SALESFORCE_UNITY_PALETTE } from "../lib/salesforceDashboard";

type WebOrders = MarketingWebOrdersQuery["marketingWebOrders"];
type WebOrder = WebOrders["orders"][number];
type WebOrderStep = WebOrder["steps"][number];

const CHART_HEIGHT = 220;
const STEP_KINDS = [
  WebOrderStepKind.LeadProducts,
  WebOrderStepKind.Conversion,
  WebOrderStepKind.Contract,
  WebOrderStepKind.Order,
];
const MINUTE_MS = 60 * 1000;
// Narrower screens scroll the table instead of wrapping every cell.
const TABLE_MIN_WIDTH = 1040;
const PRODUCT_MAX_WIDTH = 220;
// Up to this long after the order a step shows as minutes, then as hours.
const MINUTES_SHOWN = 90;
const HOURS_SHOWN = 48;

const cellSx = { fontSize: "13px", py: "8px", px: "10px", color: CRM_COLORS.text, verticalAlign: "top" } as const;
const headerSx = { ...cellSx, backgroundColor: CRM_COLORS.tableHeader, color: CRM_COLORS.textWeak, fontWeight: 700 };
const linkSx = { color: CRM_COLORS.link, textDecoration: "none", "&:hover": { textDecoration: "underline" } };
const WARNING = { color: "#8c4b02", backgroundColor: "#fef1e6", borderColor: "#fcc003" };
const STUCK = { color: "#8e030f", backgroundColor: "#feded8", borderColor: "#fe9b8b" };

const SalesforceLink = ({
  href,
  labelKey = "openInSalesforce",
}: {
  href: string | null | undefined;
  labelKey?: string;
}) => {
  const translate = useTranslate();
  if (!href) return null;
  const label = translate(`marketing.webOrders.${labelKey}`);
  return (
    <MuiTooltip title={label}>
      <IconButton
        size="small"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        sx={{ p: "2px", color: CRM_COLORS.label }}
      >
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </MuiTooltip>
  );
};

const Tile = ({ label, value, accent }: { label: string; value: number; accent: string }) => (
  <Box
    sx={{
      p: "16px",
      backgroundColor: CRM_COLORS.cardBackground,
      border: `1px solid ${CRM_COLORS.cardBorder}`,
      borderTop: `3px solid ${accent}`,
      borderRadius: "4px",
      boxShadow: CRM_COLORS.cardShadow,
    }}
  >
    <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>{label}</Typography>
    <Typography sx={{ fontSize: "28px", fontWeight: 300, lineHeight: 1.1, color: CRM_COLORS.text }}>
      {formatCrmCount(value)}
    </Typography>
  </Box>
);

// How long after the order a step happened: minutes, hours, then its day.
const useAfter = () => {
  const translate = useTranslate();
  return (createdAt: string | null | undefined, step: WebOrderStep): string => {
    if (step.on) return formatCrmDate(step.on);
    if (!step.at) return "";
    if (!createdAt) return formatCrmDateTime(step.at);
    const minutes = Math.max(0, Math.round((new Date(step.at).getTime() - new Date(createdAt).getTime()) / MINUTE_MS));
    if (minutes <= MINUTES_SHOWN) return translate("marketing.webOrders.afterMinutes", { minutes });
    const hours = Math.round(minutes / 60);
    return hours <= HOURS_SHOWN ? translate("marketing.webOrders.afterHours", { hours }) : formatCrmDayOf(step.at);
  };
};

const StepCell = ({ order, step, canOpen }: { order: WebOrder; step: WebOrderStep | undefined; canOpen: boolean }) => {
  const translate = useTranslate();
  const after = useAfter();
  if (!step?.done) {
    return order.stuck ? (
      <Typography component="span" sx={{ fontSize: "13px", fontWeight: 700, color: STUCK.color }}>
        {translate("marketing.webOrders.missing")}
      </Typography>
    ) : (
      <Typography component="span" sx={{ fontSize: "13px", color: CRM_COLORS.label }}>
        {translate("marketing.webOrders.pending")}
      </Typography>
    );
  }
  const title = step.title ?? step.salesforceId ?? "";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
        <CheckCircleIcon sx={{ fontSize: 16, color: CRM_COLORS.success }} />
        <span>{after(order.createdAt, step)}</span>
      </Box>
      {step.salesforceId && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "2px", maxWidth: 180 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {canOpen && step.object ? (
              <Link component={RouterLink} to={crmRecordPath(step.object, step.salesforceId)} sx={linkSx}>
                {title}
              </Link>
            ) : (
              title
            )}
          </Box>
          <SalesforceLink
            href={step.salesforceUrl}
            // The conversion opens its account here and its opportunity in Salesforce.
            labelKey={step.kind === WebOrderStepKind.Conversion ? "openOpportunityInSalesforce" : undefined}
          />
        </Box>
      )}
    </Box>
  );
};

const Notes = ({ order }: { order: WebOrder }) => {
  const translate = useTranslate();
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
      {order.stuck && (
        <Chip
          size="small"
          variant="outlined"
          label={translate("marketing.webOrders.markers.STUCK")}
          sx={{ height: 20, fontSize: "12px", fontWeight: 600, ...STUCK }}
        />
      )}
      {order.markers.map(marker => (
        <Chip
          key={marker}
          size="small"
          variant="outlined"
          label={translate(`marketing.webOrders.markers.${marker}`)}
          sx={{ height: 20, fontSize: "12px", fontWeight: 600, ...WARNING }}
        />
      ))}
    </Box>
  );
};

const OrdersTable = ({ orders, canOpen }: { orders: WebOrder[]; canOpen: boolean }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`marketing.webOrders.${key}`);
  return (
    <TableContainer>
      <Table size="small" aria-label={t("title")} sx={{ minWidth: TABLE_MIN_WIDTH }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>{t("created")}</TableCell>
            <TableCell sx={headerSx}>{t("customer")}</TableCell>
            <TableCell sx={headerSx}>{t("products")}</TableCell>
            {STEP_KINDS.map(kind => (
              <TableCell key={kind} sx={headerSx}>
                {t(`steps.${kind}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map(order => (
            <TableRow
              key={order.salesforceId}
              hover
              sx={order.stuck ? { backgroundColor: "#fff8f6", "&:hover": { backgroundColor: "#fdeeea" } } : undefined}
            >
              <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                {order.createdAt && formatCrmDateTime(order.createdAt)}
              </TableCell>
              <TableCell sx={cellSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "2px", whiteSpace: "nowrap" }}>
                  {canOpen ? (
                    <Link component={RouterLink} to={crmRecordPath(CrmObject.Lead, order.salesforceId)} sx={linkSx}>
                      {order.title}
                    </Link>
                  ) : (
                    order.title
                  )}
                  <SalesforceLink href={order.salesforceUrl} />
                </Box>
                {order.company && (
                  <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>{order.company}</Typography>
                )}
                <Box sx={{ mt: "4px", maxWidth: 220 }}>
                  <Notes order={order} />
                </Box>
              </TableCell>
              <TableCell sx={cellSx}>
                {order.products.map((product, index) => {
                  const quantity =
                    product.quantity === null || product.quantity === undefined ? "" : `${product.quantity}× `;
                  const line = `${quantity}${product.name ?? ""}`;
                  return (
                    <Box
                      key={`${product.name}-${index}`}
                      title={line}
                      sx={{
                        maxWidth: PRODUCT_MAX_WIDTH,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {line}
                    </Box>
                  );
                })}
                {order.products.length === 0 && order.planVariantCount > 0 && (
                  <Typography sx={{ fontSize: "12px", color: CRM_COLORS.textWeak }}>
                    {translate("marketing.webOrders.planVariants", { smart_count: order.planVariantCount })}
                  </Typography>
                )}
              </TableCell>
              {STEP_KINDS.map(kind => (
                <TableCell key={kind} sx={cellSx}>
                  <StepCell order={order} step={order.steps.find(step => step.kind === kind)} canOpen={canOpen} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const WebOrdersContent = ({ webOrders }: { webOrders: WebOrders }) => {
  const translate = useTranslate();
  const t = (key: string) => translate(`marketing.webOrders.${key}`);
  const [onlyStuck, setOnlyStuck] = useState(false);
  const { canAccess: canReadCrm } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const canOpen = canReadCrm === true;
  const shown = onlyStuck ? webOrders.orders.filter(order => order.stuck) : webOrders.orders;
  const total = webOrders.weeks.reduce((sum, week) => sum + week.orders, 0);
  const stuck = webOrders.weeks.reduce((sum, week) => sum + week.stuck, 0);
  const thisWeek = webOrders.weeks[webOrders.weeks.length - 1];
  const chart = webOrders.weeks.map(week => ({
    week: formatCrmDate(week.week).replace(/\s?\d{4}$/, ""),
    [t("orders")]: week.orders - week.stuck,
    [t("stuck")]: week.stuck,
  }));

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: "12px",
          mb: "12px",
        }}
      >
        <Tile label={t("total")} value={total} accent={CRM_OBJECT_COLORS[CrmObject.Lead]} />
        <Tile label={t("stuck")} value={stuck} accent={CRM_COLORS.error} />
        <Tile label={t("thisWeek")} value={thisWeek?.orders ?? 0} accent={CRM_COLORS.brand} />
      </Box>
      <Box sx={{ mb: "12px" }}>
        <CrmCard title={t("perWeek")}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart data={chart} margin={{ left: -16, right: 8 }}>
              <CartesianGrid vertical={false} stroke="rgba(197,211,223,0.5)" />
              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar
                dataKey={t("orders")}
                stackId="orders"
                fill={SALESFORCE_UNITY_PALETTE[0]}
                isAnimationActive={false}
              />
              <Bar dataKey={t("stuck")} stackId="orders" fill={SALESFORCE_UNITY_PALETTE[8]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </CrmCard>
      </Box>
      <CrmCard
        flush
        title={t("list")}
        actions={
          <FormControlLabel
            control={<Switch size="small" checked={onlyStuck} onChange={(_event, checked) => setOnlyStuck(checked)} />}
            label={t("onlyStuck")}
            componentsProps={{ typography: { sx: { fontSize: "13px" } } }}
          />
        }
      >
        {shown.length === 0 ? (
          <Typography sx={{ px: "16px", pb: "12px", fontSize: "13px" }}>
            {t(onlyStuck ? "emptyStuck" : "empty")}
          </Typography>
        ) : (
          <OrdersTable orders={shown} canOpen={canOpen} />
        )}
      </CrmCard>
    </>
  );
};

const WebOrdersView = () => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<MarketingWebOrdersQuery>(MarketingWebOrdersDocument, {
    fetchPolicy: "cache-and-network",
  });
  const webOrders = data?.marketingWebOrders;
  return (
    <Box data-testid="web-orders">
      <Title title={translate("marketing.webOrders.title")} />
      <CrmPageHeader
        icon={
          <Box
            aria-hidden
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "4px",
              backgroundColor: CRM_OBJECT_COLORS[CrmObject.LeadProduct],
              color: CRM_COLORS.iconForeground,
            }}
          >
            <ShoppingCartOutlinedIcon />
          </Box>
        }
        overline={translate("marketing.title")}
        title={translate("marketing.webOrders.title")}
        meta={
          webOrders && (
            <>
              {webOrders.syncedAt && `${translate("crm.syncedAt", { time: formatCrmDateTime(webOrders.syncedAt) })} • `}
              {translate("marketing.webOrders.stuckRule", { minutes: webOrders.stuckAfterMinutes })}
            </>
          )
        }
      />
      {error && <Alert severity="error">{translate("marketing.loadError")}</Alert>}
      {loading && !webOrders && <CircularProgress aria-label={translate("crm.loading")} />}
      {webOrders && (
        <Alert severity="info" sx={{ mb: "12px", fontSize: "13px" }}>
          {translate("marketing.webOrders.confirmationNotTracked")}
          {!webOrders.planVariantIdsKnown && ` ${translate("marketing.webOrders.planVariantIdsUnknown")}`}
        </Alert>
      )}
      {webOrders && <WebOrdersContent webOrders={webOrders} />}
    </Box>
  );
};

export const WebOrdersPage = () => (
  <RequirePermission permission={WEB_ORDERS_RESOURCE}>
    <WebOrdersView />
  </RequirePermission>
);
